import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CheckCircle2, XCircle, FileDown, ShieldCheck, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtMoney, fmtDate } from "@/lib/format";
import { openWaFromPublicApproval } from "@/lib/whatsapp";

/**
 * Public (no-auth) page where a client can review a quotation sent by
 * KINOS and either approve or reject it online. Reached from the email
 * CTA at `/approve/{public_token}`.
 */
export default function QuotationApprove() {
  const { token } = useParams();
  const { data: q, isLoading, error, refetch } = useQuery({
    queryKey: ["public-quo", token],
    queryFn: () => api.getPublicQuotation(token),
    retry: false,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [confirming, setConfirming] = useState(null); // 'approve' | 'reject'

  const mut = useMutation({
    mutationFn: (decision) =>
      api.decidePublicQuotation(token, {
        decision,
        approver_name: name.trim(),
        approver_email: email.trim(),
        comment: comment.trim(),
      }),
    onSuccess: (res) => {
      toast.success(
        res.status === "accepted"
          ? "Quotation approved. Thank you!"
          : "Quotation declined. KINOS has been notified."
      );
      setConfirming(null);
      refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Something went wrong"),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3">
          <XCircle className="size-10 text-red-500 mx-auto" />
          <h1 className="text-xl font-bold">Link not found</h1>
          <p className="text-sm text-muted-foreground">
            This approval link is invalid or has been revoked. Please contact
            KINOS at <a href="mailto:info@kinos.tl" className="text-primary underline">info@kinos.tl</a>.
          </p>
        </div>
      </div>
    );
  }

  const decided = q.client_decision || null;
  const terminal = ["accepted", "rejected", "converted"].includes(q.status);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quotation-approve-page">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-primary" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                KINOS — Leading Innovation Technology
              </div>
              <div className="font-semibold text-sm">Online quotation approval</div>
            </div>
          </div>
          <a
            href={`${API}/public/quotations/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
            data-testid="approve-pdf-link"
          >
            <FileDown className="size-4" /> View PDF
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {/* Status banner */}
        {terminal && (
          <div
            data-testid="approve-status-banner"
            className={`rounded-md p-4 border ${
              q.status === "accepted" || q.status === "converted"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`}
          >
            <div className="flex items-center gap-2 font-semibold">
              {q.status === "accepted" || q.status === "converted" ? (
                <CheckCircle2 className="size-5" />
              ) : (
                <XCircle className="size-5" />
              )}
              {q.status === "accepted" && "This quotation has been approved."}
              {q.status === "converted" && "This quotation has been approved and converted to an order."}
              {q.status === "rejected" && "This quotation has been declined."}
            </div>
            {decided && (
              <div className="text-xs mt-1.5 opacity-80">
                By {decided.approver_name} on {fmtDate(decided.decided_at)}
                {decided.comment ? ` — “${decided.comment}”` : ""}
              </div>
            )}
          </div>
        )}

        {/* Summary card */}
        <section className="bg-white border border-border rounded-md p-5">
          <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Quotation
              </div>
              <div className="text-lg font-bold font-mono" data-testid="approve-quo-number">
                {q.quotation_number}
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div>Issued {fmtDate(q.issue_date)}</div>
              <div>Valid until {fmtDate(q.valid_until)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Prepared for
              </div>
              <div className="font-medium">{q.client_snapshot?.name}</div>
              <div className="text-muted-foreground">
                {q.client_snapshot?.contact_person}
              </div>
              <div className="text-muted-foreground">{q.client_snapshot?.email}</div>
            </div>
            <div className="sm:text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Total
              </div>
              <div className="text-2xl font-bold text-primary font-mono">
                {fmtMoney(q.total)} {q.currency}
              </div>
              <div className="text-xs text-muted-foreground">
                Subtotal {fmtMoney(q.subtotal)} · Tax {fmtMoney(q.tax)}
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="mt-5 border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Unit price</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, idx) => (
                  <tr key={idx} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.name}</div>
                      {it.specification && (
                        <div className="text-xs text-muted-foreground">{it.specification}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{it.qty}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fmtMoney(it.unit_price)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fmtMoney(it.line_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {q.terms && (
            <div className="mt-4 text-xs text-muted-foreground">
              <span className="font-semibold uppercase tracking-widest">Terms:</span>{" "}
              {q.terms}
            </div>
          )}
        </section>

        {/* Decision form */}
        {!terminal && (
          <section className="bg-white border border-border rounded-md p-5" data-testid="approve-form">
            <h2 className="font-bold text-base mb-1">Your decision</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Once submitted, KINOS will be notified immediately. Approving is
              legally binding as an intent-to-purchase.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="overline">Your full name *</Label>
                <Input
                  data-testid="approve-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label className="overline">Your email (optional)</Label>
                <Input
                  data-testid="approve-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.tl"
                />
              </div>
            </div>
            <div className="mb-4">
              <Label className="overline">Comment (optional)</Label>
              <Textarea
                data-testid="approve-comment"
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Anything you'd like to add…"
              />
            </div>

            {confirming ? (
              <div
                data-testid="approve-confirm"
                className={`rounded p-4 border text-sm ${
                  confirming === "approve"
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <p className="font-semibold mb-2">
                  {confirming === "approve"
                    ? "Confirm approval of this quotation?"
                    : "Confirm rejection of this quotation?"}
                </p>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    disabled={mut.isPending}
                    onClick={() => setConfirming(null)}
                    data-testid="approve-confirm-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={mut.isPending || !name.trim()}
                    onClick={() => mut.mutate(confirming)}
                    data-testid="approve-confirm-submit"
                    className={
                      confirming === "approve"
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-red-600 hover:bg-red-700"
                    }
                  >
                    {mut.isPending
                      ? "Submitting…"
                      : confirming === "approve"
                      ? "Yes, approve"
                      : "Yes, reject"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 sm:mr-auto"
                  onClick={() => openWaFromPublicApproval(q, "en")}
                  data-testid="approve-wa-btn"
                  title="Ask a question via WhatsApp"
                >
                  <MessageCircle className="size-4 mr-1.5" /> Have a question? WhatsApp us
                </Button>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  disabled={!name.trim()}
                  onClick={() => setConfirming("reject")}
                  data-testid="approve-reject-btn"
                >
                  <XCircle className="size-4 mr-1.5" /> Reject
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!name.trim()}
                  onClick={() => setConfirming("approve")}
                  data-testid="approve-approve-btn"
                >
                  <CheckCircle2 className="size-4 mr-1.5" /> Approve
                </Button>
              </div>
            )}
          </section>
        )}

        <footer className="text-center text-xs text-muted-foreground pt-4">
          KINOS Unipessoal LDA · info@kinos.tl · +670 3311480 · Dili, Timor-Leste
        </footer>
      </main>
    </div>
  );
}
